/**
 * MayaAvatar — iter276 (Feb 13, 2026) · iter278.3 (Feb 14, 2026)
 *
 * The painterly portrait of Maya, JnS Creation's in-house designer.
 * Used everywhere Maya needs a "face" — header card, welcome moment,
 * "Maya's pick" ribbon tooltip, global Maya FAB upgrade.
 *
 * iter278.3 — Replaced auto-generated portrait with a hand-picked
 * customer-supplied illustration (saved as /public/maya/portrait-v1.jpg).
 * Cache-busted via ?v=2 so existing browsers fetch the new image
 * instead of serving the old cached version.
 *
 * If the file is missing for any reason (CDN miss, dev box), we fall
 * back to a tasteful rosegold monogram circle so Maya is never broken.
 */
import { useState } from "react";
import { Sparkles } from "lucide-react";
import { MAYA_PORTRAIT_URL } from "../../../../../lib/mayaPortrait";

const PORTRAIT_URL = MAYA_PORTRAIT_URL;

/** sizes: 'xs' (28), 'sm' (40), 'md' (56), 'lg' (80), 'xl' (120) */
const SIZE_PX = { xs: 28, sm: 40, md: 56, lg: 80, xl: 120 };

export default function MayaAvatar({
    size = "md",
    online = true,
    sparkle = false,
    className = "",
    testid = "maya-avatar",
}) {
    const [fallback, setFallback] = useState(false);
    const px = SIZE_PX[size] || SIZE_PX.md;

    return (
        <span
            className={`relative inline-flex items-center justify-center shrink-0 ${className}`}
            style={{ width: px, height: px }}
            data-testid={testid}
        >
            {!fallback ? (
                <img
                    src={PORTRAIT_URL}
                    alt="Maya — your in-house designer"
                    className="w-full h-full object-cover rounded-full border-2 border-white shadow-[0_10px_24px_-12px_rgba(140,80,40,0.55)]"
                    loading="eager"
                    onError={() => setFallback(true)}
                />
            ) : (
                <span
                    className="w-full h-full rounded-full flex items-center justify-center font-heading text-white shadow-md border-2 border-white"
                    style={{
                        background: "linear-gradient(135deg, #C9A47A 0%, #B26F4C 60%, #6B2C2A 100%)",
                        fontSize: px * 0.42,
                        lineHeight: 1,
                    }}
                    aria-hidden
                >
                    M
                </span>
            )}
            {online && (
                <span
                    className="absolute bg-emerald-500 rounded-full border-2 border-white"
                    style={{
                        width: Math.max(8, px * 0.18),
                        height: Math.max(8, px * 0.18),
                        right: 1,
                        bottom: 1,
                        boxShadow: "0 0 0 2px rgba(255,255,255,0.95), 0 0 0 4px rgba(16,185,129,0.25)",
                    }}
                    aria-label="Maya is online"
                />
            )}
            {sparkle && (
                <span
                    className="absolute -top-1 -right-1 text-rosegold-deep"
                    style={{
                        animation: "maya-avatar-sparkle 2.4s ease-in-out infinite",
                    }}
                    aria-hidden
                >
                    <Sparkles size={Math.max(10, px * 0.22)} />
                </span>
            )}
            <style>{`
                @keyframes maya-avatar-sparkle {
                    0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.7; }
                    50%      { transform: scale(1.25) rotate(20deg); opacity: 1; }
                }
                @media (prefers-reduced-motion: reduce) {
                    [data-testid="${testid}"] [aria-hidden] { animation: none !important; }
                }
            `}</style>
        </span>
    );
}
